import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Plus, Minus, Flame, Leaf, Wheat } from 'lucide-react';
import { Button } from './ui/button';

const MenuItem = ({ item, compact = false }) => {
  const { language, t } = useLanguage();
  const { cart, addToCart, updateQuantity } = useCart();

  const cartItem = cart.find(i => i.item_id === item.id);
  const quantity = cartItem?.quantity || 0;

  const getName = () => {
    if (language !== 'en' && item.name_translations?.[language]) {
      return item.name_translations[language];
    }
    if (language === 'fr' && item.name_fr) return item.name_fr;
    return item.name;
  };

  const getImgUrl = (url) => url || null;

  const formatPrice = (price) => {
    return `฿${price.toLocaleString()}`;
  };

  const TagBadge = ({ tag }) => {
    const tagConfig = {
      vegan: { icon: Leaf, color: 'bg-[#52B788] text-white', label: t('tags.vegan') },
      spicy: { icon: Flame, color: 'bg-[#D62828] text-white', label: t('tags.spicy') },
      gluten_free: { icon: Wheat, color: 'bg-[#F4A261] text-white', label: t('tags.gluten_free') }
    };
    
    const config = tagConfig[tag];
    if (!config) return null;
    
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (compact) {
    return (
      <div className="card p-2 animate-fade-in" data-testid={`suggestion-${item.id}`}>
        <div className="w-full aspect-square mb-2 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden"
          style={item.image_url ? { backgroundImage: `url(${getImgUrl(item.image_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
          {!item.image_url && <span className="text-lg font-bold text-[#D4A853]">{getName()?.charAt(0)}</span>}
        </div>
        <h3 className="font-semibold text-white text-xs line-clamp-1">
          {getName()}
          {item.spice_level > 0 && <span className="ml-0.5 text-red-400 text-[10px]">{'🌶'.repeat(item.spice_level)}</span>}
        </h3>
        <div className="flex items-center justify-between mt-1">
          <span className="font-bold text-[#D4A853] text-sm">{formatPrice(item.price)}</span>
          <Button onClick={() => addToCart(item)} className="btn-primary h-6 w-6 rounded-full p-0" data-testid={`add-suggestion-${item.id}`}>
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`card p-3 flex gap-3 animate-fade-in ${item.is_popular ? 'ring-2 ring-[#D4A853]/50' : ''}`}
      data-testid={`menu-item-${item.id}`}
    >
      {/* Image */}
      <div className="relative w-24 h-24 flex-shrink-0">
        <div className="w-full h-full rounded-xl bg-white/10 flex items-center justify-center overflow-hidden"
          style={item.image_url ? { backgroundImage: `url(${getImgUrl(item.image_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
          {!item.image_url && <span className="text-2xl font-bold text-[#D4A853]">{getName()?.charAt(0)}</span>}
        </div>
        {item.is_popular && (
          <span className="absolute -top-2 -left-2 popular-badge px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
            <span>&#11088;</span> {t('popular')}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <h3 className="font-semibold text-white text-sm line-clamp-1">
          {getName()}
          {item.spice_level > 0 && <span className="ml-1 text-red-400">{'🌶'.repeat(item.spice_level)}</span>}
        </h3>
        <p className="text-xs text-[#A0A0A0] line-clamp-2 mt-0.5">{item.description}</p>
        
        {/* Tags */}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {item.tags.map(tag => (
              <TagBadge key={tag} tag={tag} />
            ))}
          </div>
        )}

        {/* Price and Actions */}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="font-bold text-[#D4A853] text-base">{formatPrice(item.price)}</span>
          
          {quantity === 0 ? (
            <Button
              onClick={() => addToCart(item)}
              className="btn-primary h-8 px-3 rounded-full text-xs font-medium"
              data-testid={`add-to-cart-${item.id}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('add_to_cart')}
            </Button>
          ) : (
            <div className="flex items-center gap-2 bg-[#252525] rounded-full p-1">
              <Button
                onClick={() => updateQuantity(item.id, quantity - 1)}
                className="w-7 h-7 rounded-full bg-[#1A1A1A] hover:bg-[#D4A853] hover:text-black p-0"
                variant="ghost"
                data-testid={`decrease-${item.id}`}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-sm w-5 text-center text-white" data-testid={`quantity-${item.id}`}>
                {quantity}
              </span>
              <Button
                onClick={() => updateQuantity(item.id, quantity + 1)}
                className="w-7 h-7 rounded-full bg-[#D4A853] text-black hover:bg-[#C4983F] p-0"
                data-testid={`increase-${item.id}`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuItem;
